import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Mail, Calendar, Coins } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountOption = ({ acct, selected, onSelect }) => (
  <button
    type="button"
    data-testid={`merge-option-${acct.email}`}
    onClick={() => onSelect(acct.email)}
    className={`w-full text-left p-5 rounded-2xl border transition-colors duration-200 ${
      selected ? 'border-purple-500 bg-purple-500/10' : 'border-neutral-800 bg-neutral-950 hover:border-neutral-600'
    }`}
  >
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <Mail size={18} className="text-purple-400 shrink-0" />
        <span className="text-white font-medium truncate">{acct.email}</span>
      </div>
      <span className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${selected ? 'bg-purple-500 border-purple-500' : 'border-neutral-600'}`}>
        {selected && <Check size={14} className="text-black" />}
      </span>
    </div>
    <div className="flex gap-5 mt-3 text-sm text-neutral-400">
      <span className="flex items-center gap-1.5"><Calendar size={14} /> {acct.bookings} {acct.bookings === 1 ? "booking" : "bookings"}</span>
      <span className="flex items-center gap-1.5"><Coins size={14} /> {acct.credits} {acct.credits === 1 ? "credit" : "credits"}</span>
    </div>
  </button>
);

export default function MergeAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/account-merge/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'This link is invalid'));
  }, [token]);

  const confirm = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/account-merge/${token}/confirm`, { keep_email: choice });
      setDone(r.data);
      toast.success('Accounts merged');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setSaving(false);
    }
  };

  const totalCredits = (data?.accounts || []).reduce((s, a) => s + a.credits, 0);
  const merged = done || data?.status === 'merged';

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5 py-16" data-testid="merge-account-page">
      <div className="w-full max-w-lg">
        <p className="text-xs tracking-[0.3em] text-purple-400 mb-3">HIGH QUALITY STUDIOS</p>
        {error && <p data-testid="merge-error" className="text-red-400 text-lg">{error}</p>}
        {!error && !data && <Loader2 className="animate-spin text-purple-400" data-testid="merge-loading" />}

        {data && !merged && (
          <>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3">Pick your email</h1>
            <p className="text-neutral-400 mb-8">
              Hey {(data.name || 'there').trim().split(' ')[0]}, we found {data.accounts.length} accounts for you. Choose the email you want to keep. All bookings and <b className="text-purple-300">{totalCredits} credits</b> move into it.
            </p>
            <div className="space-y-3 mb-8">
              {data.accounts.map((a) => (
                <AccountOption key={a.email} acct={a} selected={choice === a.email} onSelect={setChoice} />
              ))}
            </div>
            <button
              data-testid="merge-confirm-btn"
              disabled={!choice || saving}
              onClick={confirm}
              className="w-full py-4 rounded-full bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-black font-bold text-lg transition-colors duration-200"
            >
              {saving ? 'Merging...' : 'Keep this email & merge'}
            </button>
          </>
        )}

        {data && merged && (
          <div data-testid="merge-success">
            <h1 className="text-4xl sm:text-5xl font-bold mb-3">You're all set</h1>
            <p className="text-neutral-400 mb-8">
              Everything now lives under <b className="text-purple-300">{done?.kept_email || data.kept_email}</b>.
              {done?.setup_password_path && ' One last step: set your password so you can log in and use your credits.'}
            </p>
            <button
              data-testid="merge-next-btn"
              onClick={() => navigate(done?.setup_password_path || '/')}
              className="w-full py-4 rounded-full bg-purple-600 hover:bg-purple-500 text-black font-bold text-lg transition-colors duration-200"
            >
              {done?.setup_password_path ? 'Set my password' : 'Go to HQ Studios'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
